"use client";
import "./news.scss";
import { useBlock } from "@/core/blocks/context";
import { Heading } from "@/core/blocks/attributes";
import { lv, localeHref } from "@/core/i18n";
import type { Post } from "@/core/types";
import type { ImageValue } from "@/core/media-shared";

/** Shows the most recent published news posts (data resolved server-side). */
export default function RecentNewsBlock() {
  const ctx = useBlock();
  const posts = (ctx.data as { posts: (Post & { coverImage?: ImageValue | null })[] } | null)?.posts || [];
  return (
    <section className="block-news">
      <div className="container">
        <Heading name="title" className="section-title" placeholder={{ nl: "Laatste nieuws", en: "Recent news" }} />
        <div className="news-grid">
          {posts.map((post) => (
            <a
              key={post.id}
              className="news-card"
              href={localeHref(ctx.locale, ctx.locales, `/news/${post.slug}`)}
            >
              {post.coverImage?.src && (
                <picture className="news-cover">
                  {post.coverImage.srcsetAvif && <source type="image/avif" srcSet={post.coverImage.srcsetAvif} sizes="(min-width: 900px) 33vw, 100vw" />}
                  {post.coverImage.srcsetWebp && <source type="image/webp" srcSet={post.coverImage.srcsetWebp} sizes="(min-width: 900px) 33vw, 100vw" />}
                  <img src={post.coverImage.src} alt={lv(post.coverImage.alt, ctx.locale, "") || ""} loading="lazy" decoding="async" />
                </picture>
              )}
              <div className="news-card-body">
                {post.published_at && (
                  <time dateTime={post.published_at}>
                    {new Date(post.published_at).toLocaleDateString(ctx.locale === "nl" ? "nl-NL" : "en-GB", { day: "numeric", month: "long", year: "numeric" })}
                  </time>
                )}
                <h3>{lv(post.title, ctx.locale, "")}</h3>
                <p>{lv(post.excerpt, ctx.locale, "")}</p>
              </div>
            </a>
          ))}
          {!posts.length && ctx.editing && (
            <p className="cms-muted">
              {lv({ nl: "Nog geen gepubliceerde nieuwsberichten — ze verschijnen hier.", en: "No published news posts yet — they will appear here." }, ctx.locale, "")}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

export const meta = { label: "Recent news", icon: "newspaper" };

export function defaults(): Record<string, unknown> {
  return { title: { nl: "Laatste nieuws" }, count: 3 };
}
